/* ==========================================================================
   PDP behaviour — variants, gallery, AJAX cart, wishlist.

   Vanilla ES2019, no dependencies, loaded with defer.

   CART INTEGRATION
   This does NOT build a drawer. The theme's cart section already listens on
   document for a bubbling `product:added` CustomEvent carrying
   { detail: { variant, quantity } } — its handler re-renders the cart and then
   calls sidebarDrawer.open(). So we do the fetch ourselves and then emit the
   theme's own event, and the theme's native drawer does the rest.

   VARIANT INTEGRATION
   The form contains exactly one control named "id": the master <select>. The
   radios are presentation; every change writes the resolved variant id into
   that select and fires `change`. That is what makes the submitted variant
   match what the customer clicked.
   ========================================================================== */

(function () {
  'use strict';

  var WISHLIST_KEY = 'pdp:wishlist';

  /* ---------------------------------------------------------------- money */

  function formatMoney(cents, format) {
    var fmt = format || '${{amount}}';
    var match = fmt.match(/\{\{\s*(\w+)\s*\}\}/);
    if (!match) return fmt;

    function group(n, precision, thousands, decimal) {
      thousands = thousands === undefined ? ',' : thousands;
      decimal = decimal === undefined ? '.' : decimal;
      if (isNaN(n) || n === null) n = 0;
      var parts = (n / 100.0).toFixed(precision).split('.');
      var whole = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      return whole + (parts[1] ? decimal + parts[1] : '');
    }

    var value;
    switch (match[1]) {
      case 'amount_no_decimals': value = group(cents, 0); break;
      case 'amount_with_comma_separator': value = group(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = group(cents, 0, '.', ','); break;
      case 'amount_with_space_separator': value = group(cents, 2, ' ', ','); break;
      case 'amount_no_decimals_with_space_separator': value = group(cents, 0, ' ', ','); break;
      default: value = group(cents, 2);
    }
    return fmt.replace(/\{\{\s*\w+\s*\}\}/, value);
  }

  function cartUrl(path) {
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return root.replace(/\/$/, '') + path;
  }

  /* --------------------------------------------------------------- wishlist */

  function readWishlist() {
    try { return JSON.parse(window.localStorage.getItem(WISHLIST_KEY)) || []; }
    catch (e) { return []; }
  }

  function writeWishlist(list) {
    try { window.localStorage.setItem(WISHLIST_KEY, JSON.stringify(list)); }
    catch (e) { /* private mode / quota — the button still toggles visually */ }
  }

  /* ------------------------------------------------------------------- PDP */

  function Pdp(root) {
    this.root = root;
    this.moneyFormat = root.getAttribute('data-money-format') || '${{amount}}';
    this.productUrl = root.getAttribute('data-product-url') || '';
    this.productId = root.getAttribute('data-product-id') || '';
    this.errorGeneric = root.getAttribute('data-error-generic') || '';

    this.variants = this.readVariants();
    this.form = root.querySelector('form[action*="/cart/add"]');
    this.master = root.querySelector('[data-pdp-master]');
    this.optionInputs = toArray(root.querySelectorAll('[data-pdp-option-input]'));

    /* Option names indexed by position - 1, matching the order of
       variant.options. Built from the rendered fieldsets so it stays correct
       whatever the merchant names or reorders. */
    this.optionNames = [];
    toArray(root.querySelectorAll('[data-pdp-option-name]')).forEach(function (set) {
      var pos = parseInt(set.getAttribute('data-pdp-option-position'), 10);
      if (!isNaN(pos)) this.optionNames[pos - 1] = set.getAttribute('data-pdp-option-name') || '';
    }, this);

    this.hideFallback();
    this.bindOptions();
    this.bindGallery();
    this.bindZoom();
    this.bindModels();
    this.bindAr();
    this.bindCustomSize();
    this.bindSticky();
    this.bindSubmit();
    this.bindWishlist();

    /* Reflect whatever is checked on load (covers ?variant= deep links).

       The gallery is deliberately NOT moved during this first pass unless the
       URL actually names a variant. A collection card shows the product's
       FEATURED image, and on 29 of 110 products that image is not variant 1's —
       on the marble tables it is a room shot while every variant image is a
       close-up of the stone. Syncing on load meant a visitor clicked a room and
       landed on a marble swatch: continuity broken at the moment of interest.

       With `?variant=` present the URL is an explicit request for that variant,
       so the gallery follows it. This matches how Shopify's own themes behave,
       and every later variant change still moves the gallery as before. */
    this.suppressGallerySync = !/[?&]variant=/.test(window.location.search);
    this.syncFromInputs(false);
    this.suppressGallerySync = false;
  }

  function toArray(nodeList) { return Array.prototype.slice.call(nodeList); }

  Pdp.prototype.readVariants = function () {
    var node = this.root.querySelector('[data-pdp-variants]');
    if (!node) return [];
    try { return JSON.parse(node.textContent) || []; } catch (e) { return []; }
  };

  /** The master select is the no-JS control; with JS it is redundant UI. */
  Pdp.prototype.hideFallback = function () {
    var fallback = this.root.querySelector('[data-pdp-fallback]');
    if (fallback) fallback.hidden = true;
  };

  /* ----- 1. variants ---------------------------------------------------- */

  Pdp.prototype.selectedOptions = function () {
    var values = [];
    this.optionInputs.forEach(function (input) {
      if (!input.checked) return;
      var pos = parseInt(input.getAttribute('data-option-position'), 10);
      values[pos - 1] = input.value;
    });
    return values;
  };

  Pdp.prototype.findVariant = function (values) {
    for (var i = 0; i < this.variants.length; i++) {
      var v = this.variants[i], ok = true;
      for (var j = 0; j < values.length; j++) {
        if (values[j] !== undefined && v.options[j] !== values[j]) { ok = false; break; }
      }
      if (ok) return v;
    }
    return null;
  };

  Pdp.prototype.bindOptions = function () {
    var self = this;
    this.optionInputs.forEach(function (input) {
      input.addEventListener('change', function () { self.syncFromInputs(true); });
    });

    // Keep the radios in step if anything writes to the master select directly
    // (the no-JS control, or a future theme integration).
    if (this.master) {
      this.master.addEventListener('change', function () {
        var v = self.variantById(Number(self.master.value));
        if (v) self.applyVariant(v, true, false);
      });
    }
  };

  Pdp.prototype.variantById = function (id) {
    for (var i = 0; i < this.variants.length; i++) {
      if (this.variants[i].id === id) return this.variants[i];
    }
    return null;
  };

  Pdp.prototype.syncFromInputs = function (pushUrl) {
    var values = this.selectedOptions();
    this.updateSelectedLabels(values);
    this.updateAvailability();

    var variant = this.findVariant(values);
    if (!variant) { this.setUnavailable(); return; }
    this.applyVariant(variant, false, pushUrl);
  };

  /**
   * The single place a variant becomes "current".
   * @param {boolean} fromMaster - skip writing back to the select we were told by
   * @param {boolean} pushUrl    - update ?variant= in the address bar
   */
  Pdp.prototype.applyVariant = function (variant, fromMaster, pushUrl) {
    // *** THE FIX ***: the control the form actually submits.
    if (this.master && !fromMaster && String(this.master.value) !== String(variant.id)) {
      this.master.value = String(variant.id);
      this.master.dispatchEvent(new Event('change', { bubbles: true }));
    }

    if (fromMaster) this.checkInputsFor(variant);

    this.updatePrice(variant);
    this.updateStickyPrice(variant);
    this.updateAtc(variant);
    this.updateGalleryForVariant(variant);
    if (pushUrl !== false) this.updateUrl(variant);

    this.currentVariant = variant;

    // The theme's own variant event, for anything else listening.
    this.root.dispatchEvent(new CustomEvent('variant:changed', {
      bubbles: true, detail: { variant: variant }
    }));
  };

  Pdp.prototype.checkInputsFor = function (variant) {
    this.optionInputs.forEach(function (input) {
      var pos = parseInt(input.getAttribute('data-option-position'), 10);
      if (variant.options[pos - 1] === input.value) input.checked = true;
    });
    this.updateSelectedLabels(variant.options);
  };

  Pdp.prototype.updateSelectedLabels = function (values) {
    var self = this;
    values.forEach(function (value, i) {
      var label = self.root.querySelector('[data-pdp-option-selected="' + (i + 1) + '"]');
      if (label && value !== undefined) label.textContent = value;
    });
  };

  Pdp.prototype.updateAvailability = function () {
    var self = this, current = this.selectedOptions();
    this.optionInputs.forEach(function (input) {
      var pos = parseInt(input.getAttribute('data-option-position'), 10);
      var probe = current.slice();
      probe[pos - 1] = input.value;

      var reachable = self.variants.some(function (v) {
        if (!v.available) return false;
        for (var j = 0; j < probe.length; j++) {
          if (j === pos - 1) { if (v.options[j] !== input.value) return false; }
          else if (probe[j] !== undefined && v.options[j] !== probe[j]) return false;
        }
        return true;
      });

      var wrapper = input.closest('.pdp__swatch, .pdp__pill');
      if (wrapper) wrapper.classList.toggle('is-unavailable', !reachable);
      input.setAttribute('aria-disabled', reachable ? 'false' : 'true');
    });
  };

  Pdp.prototype.updatePrice = function (variant) {
    var el = this.root.querySelector('[data-pdp-price]');
    if (el) el.textContent = formatMoney(variant.price, this.moneyFormat);

    this.updateInstalments(variant);
    this.updateDelivery(variant);

    var was = this.root.querySelector('[data-pdp-price-compare]');
    if (was) {
      var onSale = variant.compare_at_price && variant.compare_at_price > variant.price;
      was.hidden = !onSale;
      if (onSale) {
        var sr = was.querySelector('.pdp__sr');
        was.textContent = '';
        if (sr) was.appendChild(sr);
        was.appendChild(document.createTextNode(' ' + formatMoney(variant.compare_at_price, this.moneyFormat)));
      }
    }
  };

  Pdp.prototype.updateAtc = function (variant) {
    var button = this.root.querySelector('[data-pdp-atc]');
    if (!button) return;
    button.disabled = !variant.available;
    var label = variant.available
      ? button.getAttribute('data-add-label')
      : button.getAttribute('data-sold-out-label');
    if (label) button.textContent = label;
  };

  Pdp.prototype.setUnavailable = function () {
    var button = this.root.querySelector('[data-pdp-atc]');
    if (button) button.disabled = true;
  };

  Pdp.prototype.updateUrl = function (variant) {
    if (!window.history || !window.history.replaceState || !this.productUrl) return;
    window.history.replaceState({}, '', this.productUrl + '?variant=' + variant.id);
  };

  /* ----- 2. gallery ----------------------------------------------------- */

  Pdp.prototype.bindGallery = function () {
    var self = this;
    this.slides = toArray(this.root.querySelectorAll('[data-pdp-slide]'));
    this.thumbs = toArray(this.root.querySelectorAll('[data-pdp-thumb]'));

    this.thumbs.forEach(function (thumb) {
      thumb.addEventListener('click', function (e) {
        e.preventDefault();
        self.showMedia(thumb.getAttribute('data-media-id'));
      });
    });

    var prev = this.root.querySelector('[data-pdp-prev]');
    var next = this.root.querySelector('[data-pdp-next]');
    if (prev) prev.addEventListener('click', function (e) { e.preventDefault(); self.step(-1); });
    if (next) next.addEventListener('click', function (e) { e.preventDefault(); self.step(1); });

    /* Mobile swipe. Below 1000px pdp.css lays the stage out as a scroll-snap
       carousel instead of stacked slides, so the finger moves the gallery
       directly. The class toggling still runs — it is what drives the
       thumbnail highlight, aria-hidden and the zoom button's tab order — so
       the two have to agree: a swipe tells showMedia which slide won, and
       showMedia scrolls the stage when something else (a thumbnail, an arrow,
       a variant change) picks one. */
    this.stage = this.root.querySelector('.pdp__stage');

    if (this.stage) {
      var scrollTick = null;
      this.stage.addEventListener('scroll', function () {
        if (scrollTick) return;
        scrollTick = setTimeout(function () {
          scrollTick = null;
          var landed = self.slideNearestCentre();
          if (!landed) return;
          var id = landed.getAttribute('data-media-id');
          /* Only when it actually changed, or showMedia's own scroll would
             re-enter this handler and the two would chase each other. */
          if (id && self.slides[self.activeIndex()] !== landed) self.showMedia(id);
        }, 120);
      }, { passive: true });
    }
  };

  /* Compared with getBoundingClientRect rather than scrollLeft on purpose:
     scrollLeft runs negative in RTL and its sign has been a recurring source of
     bugs here, while rects are direction-agnostic. */
  Pdp.prototype.slideNearestCentre = function () {
    if (!this.stage || !this.slides.length) return null;
    var stageRect = this.stage.getBoundingClientRect();
    var middle = stageRect.left + stageRect.width / 2;
    var best = null;
    var bestDistance = Infinity;

    this.slides.forEach(function (slide) {
      var rect = slide.getBoundingClientRect();
      var distance = Math.abs(rect.left + rect.width / 2 - middle);
      if (distance < bestDistance) {
        bestDistance = distance;
        best = slide;
      }
    });

    return best;
  };

  Pdp.prototype.activeIndex = function () {
    for (var i = 0; i < this.slides.length; i++) {
      if (this.slides[i].classList.contains('is-active')) return i;
    }
    return 0;
  };

  Pdp.prototype.step = function (delta) {
    if (!this.slides.length) return;
    var i = (this.activeIndex() + delta + this.slides.length) % this.slides.length;
    this.showMedia(this.slides[i].getAttribute('data-media-id'));
  };

  Pdp.prototype.showMedia = function (mediaId) {
    if (!mediaId) return;
    this.slides.forEach(function (slide) {
      var active = slide.getAttribute('data-media-id') === mediaId;
      slide.classList.toggle('is-active', active);
      if (active) slide.removeAttribute('aria-hidden');
      else slide.setAttribute('aria-hidden', 'true');

      // A hidden slide's zoom button must leave the tab order, or keyboard users
      // tab into a control they cannot see.
      var trigger = slide.querySelector('[data-pdp-zoom]');
      if (trigger) {
        if (active) trigger.removeAttribute('tabindex');
        else trigger.setAttribute('tabindex', '-1');
      }
    });
    this.thumbs.forEach(function (thumb) {
      var active = thumb.getAttribute('data-media-id') === mediaId;
      if (active) thumb.setAttribute('aria-current', 'true');
      else thumb.removeAttribute('aria-current');
      if (active && thumb.scrollIntoView) {
        thumb.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      }
    });

    /* Where the stage scrolls (mobile), the class alone no longer changes what
       is on screen — the slide has to be brought into view as well. Guarded by
       "is it already the centred one" so the swipe handler calling in here
       cannot bounce the scroll back, and so the first paint does not jump. */
    if (this.stage && this.stage.scrollWidth > this.stage.clientWidth + 1) {
      var target = null;
      for (var i = 0; i < this.slides.length; i++) {
        if (this.slides[i].getAttribute('data-media-id') === mediaId) { target = this.slides[i]; break; }
      }
      if (target && target !== this.slideNearestCentre() && target.scrollIntoView) {
        target.scrollIntoView({ block: 'nearest', inline: 'center', behavior: 'smooth' });
      }
    }
  };

  Pdp.prototype.updateGalleryForVariant = function (variant) {
    /* Set only for the duration of the constructor's first sync, and only when
       the URL carries no ?variant= — see the note at that call site. The guard
       lives here rather than at the call site because applyVariant re-enters
       through the master select's change event, so there are two paths in. */
    if (this.suppressGallerySync) return;
    if (!variant.featured_media || !variant.featured_media.id) return;
    this.showMedia(String(variant.featured_media.id));
  };

  /* ----- 2b. lightbox ---------------------------------------------------
     Full-screen zoom on the PhotoSwipe core already bundled in theme.min.js.

     Prestige does NOT expose PhotoSwipeUI_Default; it renders its own UI bar and
     binds it by hand inside a class that is hard-wired to a Flickity instance our
     stacked-slide gallery does not have. So we pass `false` for the UI module and
     supply the same bindings ourselves against the theme's own markup contract.

     If the core is missing for any reason we leave the triggers unbound rather
     than wiring a click that visibly does nothing.
     --------------------------------------------------------------------- */

  Pdp.prototype.bindZoom = function () {
    var self = this;

    // The overlay and its data live OUTSIDE .pdp (the reset would strip them),
    // so these are document-level lookups, not root-level.
    this.pswpEl = document.querySelector('[data-pdp-pswp]');
    this.zoomItems = [];

    var data = document.querySelector('[data-pdp-zoom-items]');
    if (data) {
      try { this.zoomItems = JSON.parse(data.textContent) || []; } catch (e) { this.zoomItems = []; }
    }

    if (!this.pswpEl || !this.zoomItems.length || typeof window.PhotoSwipe !== 'function') return;

    toArray(this.root.querySelectorAll('[data-pdp-zoom]')).forEach(function (trigger) {
      trigger.addEventListener('click', function (e) {
        e.preventDefault();
        self.openZoom(trigger.getAttribute('data-media-id'), trigger);
      });
    });
  };

  /** Index into zoomItems for a media id. Videos and 3D models are not in that
      list, so slide index and item index are not interchangeable. */
  Pdp.prototype.zoomIndexOf = function (mediaId) {
    for (var i = 0; i < this.zoomItems.length; i++) {
      if (String(this.zoomItems[i].id) === String(mediaId)) return i;
    }
    return 0;
  };

  Pdp.prototype.openZoom = function (mediaId, trigger) {
    var self = this;
    if (this.pswp) return;                       // already open — ignore repeat clicks

    var el = this.pswpEl;
    var ui = el.querySelector('.pswp__ui');

    var options = {
      index: this.zoomIndexOf(mediaId),
      history: false,                            // never push #&gid= onto the PDP url
      focus: true,
      escKey: true,
      arrowKeys: true,
      showHideOpacity: true,
      bgOpacity: 0.95,
      closeOnScroll: false,
      closeOnVerticalDrag: true,                 // the expected gesture on mobile
      tapToClose: false,                         // handled explicitly below
      clickToCloseNonZoomable: false,

      // Animate out of the image the customer actually clicked.
      getThumbBoundsFn: function (index) {
        var item = self.zoomItems[index];
        if (!item) return null;
        var slide = self.root.querySelector('[data-pdp-slide][data-media-id="' + item.id + '"]');
        var img = slide && slide.querySelector('img');
        if (!img) return null;
        var r = img.getBoundingClientRect();
        if (!r.width) return null;
        var scrollY = window.pageYOffset || document.documentElement.scrollTop || 0;
        return { x: r.left, y: r.top + scrollY, w: r.width };
      }
    };

    var pswp = new window.PhotoSwipe(el, false, this.zoomItems, options);
    this.pswp = pswp;
    this.zoomTrigger = trigger || null;

    // The theme's skin keeps the UI bar hidden behind .pswp__ui--hidden, which
    // PhotoSwipeUI_Default would normally clear. No UI module here, so we do it.
    if (ui) ui.classList.remove('pswp__ui--hidden');

    el.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add('pdp-scroll-locked');

    // --- chrome bindings (what PhotoSwipeUI_Default would have provided) ---
    var close = el.querySelector('[data-pswp-close]');
    var prev = el.querySelector('[data-pswp-prev]');
    var next = el.querySelector('[data-pswp-next]');

    function onClose(e) { e.preventDefault(); pswp.close(); }
    function onPrev(e) { e.preventDefault(); pswp.prev(); }
    function onNext(e) { e.preventDefault(); pswp.next(); }

    /* Bound on touchend AS WELL AS click, and that is the whole bug on a phone.

       `.pswp` carries `touch-action: none` and PhotoSwipe's gesture handler
       calls preventDefault() across the overlay to implement swipe and pinch.
       A prevented touch is never turned into a synthesized click, so a
       click-only listener never fires on a touch device — the X and the arrows
       worked with a mouse and did nothing under a finger.

       This is precisely why PhotoSwipeUI_Default binds taps rather than clicks;
       these handlers replace that module (see the note above) and had inherited
       only half of its job.

       preventDefault() inside the touchend listener suppresses the synthesized
       click that would otherwise follow, so the handler cannot run twice. Mouse
       users still get `click`, and so do keyboard users pressing Enter or Space
       on the <button> — which is why `click` has to stay. */
    function bindTap(el, handler) {
      if (!el) return;
      el.addEventListener('touchend', function (e) {
        e.preventDefault();
        handler(e);
      });
      el.addEventListener('click', handler);
    }

    bindTap(close, onClose);
    bindTap(prev, onPrev);
    bindTap(next, onNext);

    // Click-outside-to-close.
    //
    // Decided by GEOMETRY, not by event target. Two traps make the obvious
    // implementations wrong:
    //   * .pswp__bg is a SIBLING of .pswp__scroll-wrap, so a listener on the
    //     scroll-wrap never sees clicks that land on the background. The listener
    //     therefore goes on the .pswp root.
    //   * PhotoSwipe keeps the adjacent slides' <img> elements in the layer, so
    //     "bail out if the target is an IMG" also swallows clicks in the empty
    //     gutter beside a letterboxed photo — verified: elementFromPoint at the
    //     far-left edge reports .pswp__img.
    //
    // So: close unless the pointer is actually inside the CURRENT photo's
    // rendered rectangle. Drag/swipe is unaffected — PhotoSwipe suppresses the
    // synthetic click that would otherwise follow a drag.
    function onBackdrop(e) {
      if (e.target.closest && e.target.closest('.pswp__button')) return;

      // While zoomed in, EVERY click is a zoom-out — never a close. Once the
      // photo is magnified it overflows the viewport, so the "is the pointer
      // inside the image" test below stops being meaningful and would dismiss
      // the lightbox on the click that was meant to zoom back out.
      if (typeof pswp.getZoomLevel === 'function' && pswp.currItem) {
        var initial = pswp.currItem.initialZoomLevel || 0;
        if (pswp.getZoomLevel() > initial + 0.01) {
          if (typeof pswp.toggleDesktopZoom === 'function') {
            pswp.toggleDesktopZoom({ x: e.clientX, y: e.clientY });
          }
          return;
        }
      }

      var current = pswp.currItem;
      var img = current && current.container && current.container.querySelector('img');
      if (img) {
        var r = img.getBoundingClientRect();
        if (r.width &&
            e.clientX >= r.left && e.clientX <= r.right &&
            e.clientY >= r.top && e.clientY <= r.bottom) {
          // Inside the photo: zoom rather than close. toggleDesktopZoom flips
          // between fit and the item's zoom level, centred on the pointer, so
          // one click zooms in and the next zooms back out.
          if (typeof pswp.toggleDesktopZoom === 'function') {
            pswp.toggleDesktopZoom({ x: e.clientX, y: e.clientY });
          }
          return;
        }
      }
      pswp.close();
    }
    el.addEventListener('click', onBackdrop);

    // Track the index as the customer navigates. Read here rather than during
    // teardown: by then the instance is being dismantled and getCurrentIndex()
    // is not reliable.
    var lastIndex = options.index;
    pswp.listen('afterChange', function () {
      var i = pswp.getCurrentIndex();
      if (typeof i === 'number') lastIndex = i;
    });

    // --- teardown ---
    //
    // PhotoSwipe invokes listeners synchronously inside its own destroy chain, so
    // an exception thrown here aborts ITS cleanup too: the overlay freezes
    // mid-close with .pswp--open still set and Escape stops working. That is not
    // hypothetical — a stale variable reference in this very handler caused
    // exactly that. Everything below is therefore wrapped defensively.
    pswp.listen('destroy', function () {
      try {
        if (close) close.removeEventListener('click', onClose);
        if (prev) prev.removeEventListener('click', onPrev);
        if (next) next.removeEventListener('click', onNext);
        el.removeEventListener('click', onBackdrop);
        if (ui) ui.classList.add('pswp__ui--hidden');

        el.setAttribute('aria-hidden', 'true');
        document.documentElement.classList.remove('pdp-scroll-locked');

        // Leave the inline gallery on whatever the customer navigated to inside
        // the overlay, so closing does not feel like it lost their place.
        var item = self.zoomItems[lastIndex];
        if (item) self.showMedia(String(item.id));

        // Focus must come back out of a dialog that no longer exists.
        if (self.zoomTrigger) {
          var back = self.root.querySelector('[data-pdp-slide].is-active [data-pdp-zoom]') || self.zoomTrigger;
          if (back && back.focus) back.focus();
        }
      } catch (e) {
        // Never let our bookkeeping break the viewer's own teardown.
      } finally {
        self.pswp = null;
        self.zoomTrigger = null;
      }
    });

    pswp.init();
  };

  /* ----- 2c. 3D models --------------------------------------------------
     Shopify ships the <model-viewer> element itself, but its control bar is a
     CONSTRUCTOR the theme has to instantiate: `new Shopify.ModelViewerUI(el)`.
     Prestige 1.1.1 predates 3D media entirely, so nothing in the theme does it.

     This matters beyond cosmetics. We request the model with
     reveal: 'interaction', and it is that UI which draws the poster/play
     affordance triggering the reveal. Without it the customer sees a still
     image with no visible way to start the model — it only responds to a drag
     they have no reason to attempt.

     If the UI script is unavailable we leave the bare <model-viewer>, which is
     still interactive on drag. Degraded, never broken.
     --------------------------------------------------------------------- */

  Pdp.prototype.bindModels = function () {
    var models = toArray(this.root.querySelectorAll('model-viewer'));
    if (!models.length) return;

    function activate() {
      if (!window.Shopify || typeof window.Shopify.ModelViewerUI !== 'function') return false;

      models.forEach(function (el) {
        if (el.getAttribute('data-pdp-model-ready') === 'true') return;
        try {
          new window.Shopify.ModelViewerUI(el);
          el.setAttribute('data-pdp-model-ready', 'true');
        } catch (e) {
          // Leave the element as-is rather than half-wrapping it.
        }
      });
      return true;
    }

    // pdp.js and the XR scripts are all `defer`, and the XR tags come LATER in
    // the document, so ModelViewerUI is guaranteed not to exist yet right here.
    if (activate()) return;

    window.addEventListener('load', function () {
      if (activate()) return;

      // The script may still be in flight; poll briefly, then give up quietly.
      var tries = 0;
      var timer = window.setInterval(function () {
        if (activate() || ++tries > 20) window.clearInterval(timer);
      }, 150);
    });
  };

  /* ----- 2d. AR — "view it in your home" --------------------------------
     One button, two behaviours:

       AR-capable phone -> Shopify's shopify-xr.js takes the click and launches
                           Quick Look (iOS) or Scene Viewer (Android). We must
                           NOT preventDefault, or we break the thing we want.
       anything else    -> we show a QR of this product's URL so the customer
                           can carry on from their phone.

     Capability detection, best signal first:

       1. shopify-xr.js REMOVES data-shopify-xr-hidden from [data-shopify-xr]
          elements on devices it can actually serve. The gallery pill ships with
          that attribute, so its absence is Shopify's own verdict. Our CTA
          deliberately never carries it — it has to stay visible on desktop to
          offer the QR — which is why we read the pill, not ourselves.
       2. <model-viewer>.canActivateAR, once the element has upgraded.
       3. Platform feature detection: rel="ar" support is the iOS Quick Look
          signal; Android handles Scene Viewer's intent:// URLs.

     Any one of them saying yes is enough; we would rather hand a real phone to
     Shopify than show it a QR code of the page it is already on.
     --------------------------------------------------------------------- */

  /**
   * How confident are we that this device can really do AR?
   *
   *   'strong' — something that actually inspected the device said yes.
   *   'weak'   — only a user-agent string said yes, which any desktop browser
   *              in device-emulation mode will also claim.
   *   'none'   — no AR here.
   *
   * The distinction matters because a UA string LIES. Chrome DevTools with an
   * Android device preset reports an Android UA on a laptop, and treating that
   * as proof handed the click to Shopify's XR script, which then could not
   * launch anything — a button that silently did nothing. Measured:
   *
   *     desktop                 ua:no   pill:no  canActivateAR:no  -> QR  (ok)
   *     responsive, desktop ua  ua:no   pill:no  canActivateAR:no  -> QR  (ok)
   *     device preset, moble ua ua:YES  pill:no  canActivateAR:no  -> dead end
   *
   * Note the emulated case: Shopify's own detection and model-viewer BOTH said
   * no. Only the UA said yes. So a UA match alone is never enough to commit.
   */
  Pdp.prototype.arSignal = function () {
    // Shopify's XR script removes this attribute on devices it can serve.
    // That is a real inspection, not a guess.
    var pill = document.querySelector('[data-shopify-xr]:not([data-pdp-ar])');
    if (pill && !pill.hasAttribute('data-shopify-xr-hidden')) return 'strong';

    // model-viewer's own computed capability.
    var mv = this.root.querySelector('model-viewer');
    if (mv && mv.canActivateAR) return 'strong';

    // rel="ar" support is genuine feature detection for iOS Quick Look, but
    // Safari reports it broadly, so treat it as weak.
    var a = document.createElement('a');
    if (a.relList && a.relList.supports) {
      try { if (a.relList.supports('ar')) return 'weak'; } catch (e) { /* older browser */ }
    }

    if (/android/i.test(window.navigator.userAgent)) return 'weak';

    return 'none';
  };

  Pdp.prototype.bindAr = function () {
    var self = this;

    this.arButton = this.root.querySelector('[data-pdp-ar]');
    this.qr = this.root.querySelector('[data-pdp-qr]');
    if (!this.arButton) return;

    this.arButton.addEventListener('click', function (event) {
      var signal = self.arSignal();

      if (signal === 'none') {
        event.preventDefault();
        self.openQr();
        return;
      }

      // Hand the click to Shopify's XR script. On a weak signal we also arm a
      // safety net, so a device that merely CLAIMS AR can never leave the
      // customer staring at a button that did nothing.
      if (signal === 'weak') self.armArFallback();
    });

    // Arrivals from the QR. Runs before the `qr` guard below, because the deep
    // link is about launching AR, not about the modal.
    this.handleArDeepLink();

    if (!this.qr) return;

    var closeBtn = this.qr.querySelector('[data-pdp-qr-close]');
    if (closeBtn) closeBtn.addEventListener('click', function () { self.closeQr(); });

    // Click outside the panel dismisses.
    this.qr.addEventListener('click', function (event) {
      if (!event.target.closest('[data-pdp-qr-panel]')) self.closeQr();
    });

    document.addEventListener('keydown', function (event) {
      if (self.qr.hidden) return;

      if (event.key === 'Escape' || event.key === 'Esc') {
        event.preventDefault();
        self.closeQr();
        return;
      }

      // Minimal focus trap. The panel holds a single focusable control, so
      // keeping Tab on it is enough to stop focus escaping to the page behind.
      if (event.key === 'Tab' && closeBtn) {
        event.preventDefault();
        closeBtn.focus();
      }
    });
  };

  /**
   * Safety net for a weak AR signal.
   *
   * We have just let Shopify's XR script take the click. On a real phone the AR
   * viewer takes over the screen, which backgrounds the page — so a blur or a
   * visibilitychange is our proof that AR really launched, and we stand down.
   *
   * If neither fires, nothing took over: the device claimed AR it does not have.
   * We open the QR instead, so the click always produces something.
   */
  Pdp.prototype.armArFallback = function () {
    var self = this;

    if (this.arFallbackTimer) window.clearTimeout(this.arFallbackTimer);

    function standDown() {
      window.clearTimeout(self.arFallbackTimer);
      self.arFallbackTimer = null;
      window.removeEventListener('blur', standDown);
      document.removeEventListener('visibilitychange', onVisibility);
    }

    function onVisibility() {
      if (document.hidden) standDown();
    }

    window.addEventListener('blur', standDown);
    document.addEventListener('visibilitychange', onVisibility);

    this.arFallbackTimer = window.setTimeout(function () {
      standDown();
      if (self.qr && self.qr.hidden) self.openQr();
    }, 1500);
  };

  /* ----- 2e. AR deep link (?ar=1) ---------------------------------------
     The QR encodes {product_url}?ar=1. Scanning the bare product URL would only
     drop the customer on the PDP and leave them hunting for the button; the
     whole point of the QR is to arrive IN augmented reality.

     One QR cannot serve both platforms directly — iOS Quick Look launches from
     a .usdz file, Android Scene Viewer needs an intent:// URL carrying the
     .glb. So the QR points at the page, and the page routes by platform.

     Both files come from the ModelJson manifest Shopify already emits for the
     XR script, so there is no extra Liquid and no second source of truth.
     --------------------------------------------------------------------- */

  /** {usdz, glb} absolute URLs for the first model, or null. */
  Pdp.prototype.arSources = function () {
    var node = document.querySelector('[id^="ModelJson-"]');
    if (!node) return null;

    var models;
    try { models = JSON.parse(node.textContent); } catch (e) { return null; }
    if (!models || !models.length || !models[0].sources) return null;

    function absolute(url) {
      if (!url) return null;
      // Shopify emits these protocol-relative.
      return url.indexOf('//') === 0 ? window.location.protocol + url : url;
    }

    var out = { usdz: null, glb: null, title: models[0].alt || document.title };
    models[0].sources.forEach(function (s) {
      if (s.format === 'usdz') out.usdz = absolute(s.url);
      if (s.format === 'glb') out.glb = absolute(s.url);
    });
    return out;
  };

  Pdp.prototype.isIos = function () {
    var ua = window.navigator.userAgent;
    if (/iPad|iPhone|iPod/.test(ua)) return true;
    // iPadOS 13+ reports itself as a Mac; touch points give it away.
    return window.navigator.platform === 'MacIntel' && window.navigator.maxTouchPoints > 1;
  };

  /**
   * Launch the platform's AR viewer. Returns true if we actually tried.
   * Must be called from a user gesture where possible — both platforms may
   * refuse a cold navigation.
   */
  Pdp.prototype.launchAr = function () {
    var sources = this.arSources();
    if (!sources) return false;

    // --- iOS: AR Quick Look ---
    // Safari opens Quick Look when an <a rel="ar"> containing an <img> is
    // activated. The child img is required; without it Safari navigates to the
    // file instead of opening the AR viewer.
    if (this.isIos() && sources.usdz) {
      var anchor = document.createElement('a');
      anchor.setAttribute('rel', 'ar');
      anchor.setAttribute('href', sources.usdz);
      anchor.appendChild(document.createElement('img'));
      anchor.style.display = 'none';
      document.body.appendChild(anchor);
      anchor.click();
      window.setTimeout(function () {
        if (anchor.parentNode) anchor.parentNode.removeChild(anchor);
      }, 1000);
      return true;
    }

    // --- Android: Scene Viewer ---
    // browser_fallback_url matters: without it, a device that cannot resolve
    // the intent lands on a blank page instead of coming back to the product.
    if (/android/i.test(window.navigator.userAgent) && sources.glb) {
      var fallback = window.location.origin + window.location.pathname;
      var intent =
        'intent://arvr.google.com/scene-viewer/1.0' +
        '?file=' + encodeURIComponent(sources.glb) +
        '&mode=ar_preferred' +
        '&title=' + encodeURIComponent(sources.title) +
        '#Intent;scheme=https;package=com.google.ar.core;' +
        'action=android.intent.action.VIEW;' +
        'S.browser_fallback_url=' + encodeURIComponent(fallback) + ';end;';
      window.location.href = intent;
      return true;
    }

    return false;
  };

  /**
   * Handle an arrival from the QR: {product_url}?ar=1.
   *
   * A cold page load has no user activation, so the launch can be refused. We
   * try anyway — it works in the common case — and if we are still on the page
   * shortly afterwards we put the AR button in front of the customer so it is
   * one tap rather than a hunt.
   */
  Pdp.prototype.handleArDeepLink = function () {
    var self = this;
    if (!/[?&]ar=1\b/.test(window.location.search)) return;

    // Strip the flag so a refresh or back-navigation does not relaunch.
    if (window.history && window.history.replaceState) {
      var clean = window.location.pathname +
        window.location.search.replace(/([?&])ar=1&?/, '$1').replace(/[?&]$/, '') +
        window.location.hash;
      window.history.replaceState({}, '', clean);
    }

    var launched = this.launchAr();

    // Whether the launch was refused or the customer backed out of AR, leave
    // them looking at the button rather than at an arbitrary scroll position.
    window.setTimeout(function () {
      if (document.hidden) return;              // AR took over — nothing to do
      if (!self.arButton) return;
      if (self.arButton.scrollIntoView) {
        self.arButton.scrollIntoView({ block: 'center', behavior: 'smooth' });
      }
      if (!launched && self.qr && self.qr.hidden) {
        // Not an AR device at all (someone shared the link) — the QR is still
        // the useful thing to show.
        self.openQr();
      }
    }, 2000);
  };

  Pdp.prototype.openQr = function () {
    if (!this.qr) return;

    // Fetch the QR only on first open, so visitors who never click it never hit
    // the third-party endpoint.
    var img = this.qr.querySelector('[data-pdp-qr-image]');
    if (img && !img.getAttribute('src')) {
      img.setAttribute('src', img.getAttribute('data-qr-src'));
    }

    this.qrOpener = document.activeElement;
    this.qr.hidden = false;
    document.documentElement.classList.add('pdp-scroll-locked');

    var closeBtn = this.qr.querySelector('[data-pdp-qr-close]');
    if (closeBtn && closeBtn.focus) closeBtn.focus();
  };

  Pdp.prototype.closeQr = function () {
    if (!this.qr || this.qr.hidden) return;

    this.qr.hidden = true;
    document.documentElement.classList.remove('pdp-scroll-locked');

    // Focus must not be left on a control inside a hidden dialog.
    var back = this.qrOpener && this.qrOpener.focus ? this.qrOpener : this.arButton;
    if (back && back.focus) back.focus();
    this.qrOpener = null;
  };

  /* ----- 2f. custom dimensions (line item properties) --------------------
     The fieldset is revealed when any selected option value equals the
     made-to-measure trigger (a section setting, default "התאמה אישית").

     Visibility alone is not enough: a hidden-but-enabled input still posts, so
     every ordinary variant would carry empty אורך/עומק properties into the
     order. The inputs therefore ship `disabled` and are enabled only while the
     fieldset is shown — a disabled control is never serialised.
     --------------------------------------------------------------------- */

  Pdp.prototype.bindCustomSize = function () {
    var self = this;
    this.customSize = this.root.querySelector('[data-pdp-custom-size]');
    if (!this.customSize) return;

    this.customTrigger = (this.customSize.getAttribute('data-trigger-value') || '').trim();
    // Which option the trigger belongs to. Blank keeps the old any-option behaviour.
    this.customOption = (this.customSize.getAttribute('data-trigger-option') || '').trim();
    this.customInputs = toArray(this.customSize.querySelectorAll('[data-pdp-custom-input]'));

    // Re-evaluate whenever the variant changes, however it was changed.
    this.root.addEventListener('variant:changed', function () { self.syncCustomSize(); });
    this.syncCustomSize();
  };

  /** Show/hide + enable/disable, driven by the currently selected option values. */
  Pdp.prototype.syncCustomSize = function () {
    if (!this.customSize) return;

    var trigger = this.customTrigger;
    var show = false;

    if (trigger) {
      var values = this.currentVariant && this.currentVariant.options
        ? this.currentVariant.options
        : this.selectedOptions();

      if (this.customOption) {
        /* Scoped to one option.

           RONI carries "התאמה אישית" on BOTH גוון and מידות, so the old scan
           across every value opened the measurement fields as soon as a custom
           COLOUR was chosen — the fields looked stuck open on every variant.
           product.options gives the option NAMES in the same order as
           variant.options gives the values, so the index lines them up. */
        var names = this.optionNames || [];
        for (var n = 0; n < values.length; n++) {
          if (String(names[n] || '').trim() !== this.customOption) continue;
          if (values[n] && String(values[n]).trim() === trigger) show = true;
          break;
        }
      } else {
        for (var i = 0; i < values.length; i++) {
          if (values[i] && String(values[i]).trim() === trigger) { show = true; break; }
        }
      }
    }

    if (show === this.customVisible) return;   // nothing to do
    this.customVisible = show;

    this.customSize.hidden = !show;
    this.customInputs.forEach(function (input) {
      input.disabled = !show;
      if (!show) input.value = '';             // never carry stale numbers forward
    });
  };

  /** Localised "fill in length and depth" message, carried on the fieldset. */
  Pdp.prototype.customRequiredMessage = function () {
    return (this.customSize && this.customSize.getAttribute('data-required-message')) ||
           this.errorGeneric;
  };

  /** {"אורך": "220", ...} for the filled fields; {} when the fieldset is off. */
  Pdp.prototype.customProperties = function () {
    var props = {};
    if (!this.customSize || this.customSize.hidden) return props;

    this.customInputs.forEach(function (input) {
      var value = (input.value || '').trim();
      if (!value) return;
      // name="properties[אורך]" -> "אורך"
      var match = (input.getAttribute('name') || '').match(/^properties\[(.+)\]$/);
      if (match) props[match[1]] = value;
    });
    return props;
  };

  /* ----- 2g. sticky add to cart -----------------------------------------
     Mirrors Article's behaviour: the bar appears once the real Add to cart
     button leaves the viewport and hides again when it returns.

     IntersectionObserver rather than a scroll handler — the browser does the
     measuring off the main thread, so there is no scroll-time layout thrash.
     --------------------------------------------------------------------- */

  Pdp.prototype.bindSticky = function () {
    var self = this;
    this.sticky = this.root.querySelector('[data-pdp-sticky]');
    var anchor = this.root.querySelector('[data-pdp-atc]');
    if (!this.sticky || !anchor) return;

    var stickyBtn = this.sticky.querySelector('[data-pdp-sticky-atc]');
    if (stickyBtn) {
      // Same code path as the main button: same variant, same properties, same
      // product:added event. Not a second cart implementation.
      stickyBtn.addEventListener('click', function () { self.addToCart(stickyBtn); });
    }

    if (!('IntersectionObserver' in window)) return;   // no bar rather than a broken one

    // `!isIntersecting` is TRUE in two opposite situations, and conflating them
    // was a real bug: the bar appeared at page load.
    //
    //   ATC above the viewport  -> customer scrolled past it  -> show the bar
    //   ATC below the viewport  -> they have not reached it yet -> stay hidden
    //
    // On a product with several option groups the ATC starts below the fold, so
    // the second case fires immediately on load. boundingClientRect.top tells
    // the two apart: negative means the element has gone off the TOP.
    // Measure the anchor LIVE in the callback rather than trusting
    // entry.boundingClientRect: that rect is a snapshot from when the
    // intersection was recorded, and during a fast or programmatic scroll it
    // arrives stale — which produced a bar that un-hid but never slid in.
    //
    // `bottom <= 0` means the button has left through the TOP of the viewport,
    // i.e. the customer scrolled past it. Not-intersecting alone is ambiguous:
    // it is equally true for a button still below the fold on page load, which
    // is what made the bar appear at scroll position 0.
    function evaluateSticky() {
      var rect = anchor.getBoundingClientRect();
      self.toggleSticky(rect.bottom <= 0);
    }

    this.stickyObserver = new IntersectionObserver(function () {
      evaluateSticky();
    }, { threshold: 0, rootMargin: '0px' });

    this.stickyObserver.observe(anchor);

    // Safety net. IntersectionObserver only fires when the ratio CROSSES a
    // threshold; a jump straight from "below the fold" to "scrolled past" never
    // becomes intersecting, so it can produce no callback at all. The listener
    // is passive and rAF-throttled, so it does no work per scroll event beyond
    // scheduling one frame.
    var queued = false;
    window.addEventListener('scroll', function () {
      if (queued) return;
      queued = true;
      window.requestAnimationFrame(function () {
        queued = false;
        evaluateSticky();
      });
    }, { passive: true });

    // Establish the correct state for the position the page actually loaded at
    // (a refresh can restore a mid-page scroll).
    evaluateSticky();
  };

  /**
   * Idempotent and synchronous, deliberately.
   *
   * The first version deferred adding `.is-visible` to a requestAnimationFrame
   * and guarded on a cached `stickyVisible` flag. If a second call landed
   * between the schedule and the frame, the flag and the DOM disagreed — and
   * because the guard trusted the flag, they never re-synced. The bar latched
   * into "un-hidden but never slid in" and stopped responding to scroll.
   *
   * No cached state, no deferral: the class is derived from `show` every time,
   * so any call repairs the DOM rather than depending on it.
   */
  Pdp.prototype.toggleSticky = function (show) {
    if (!this.sticky) return;

    // The `hidden` attribute exists only to stop a flash before JS boots; a
    // display:none element cannot transition, so drop it once and let the
    // transform (plus visibility, see pdp.css) do the hiding from here on.
    //
    // The forced reflow is not superstition. Going display:none -> block and
    // adding the class in the same frame leaves the transition with no start
    // value, so the transform does not animate and the element keeps reporting
    // its previous offset until something else triggers a recalculation —
    // measured as the bar lagging exactly one scroll step behind its class.
    if (this.sticky.hidden) {
      this.sticky.hidden = false;
      void this.sticky.offsetHeight;      // flush layout, establishing the "from" value
    }

    this.sticky.classList.toggle('is-visible', !!show);
  };

  /** Keep the sticky price in step with the chosen variant. */
  /* ----- instalment line -------------------------------------------------
     Deliberately called from inside updatePrice() rather than bound to
     `variant:changed` separately. One update path means the monthly figure and
     the price it divides can never disagree — if the price moved, this moved.

     It also reuses formatMoney() and this.moneyFormat, so the currency renders
     exactly as it does everywhere else on the page rather than through a second
     formatter that would drift.

     Math.floor mirrors Liquid's `divided_by`, which is integer division on the
     minor-unit price. Keeping the two identical means the server-rendered first
     paint and every later update produce the same string — no flicker on the
     first variant change, and no off-by-one between them.
     --------------------------------------------------------------------- */
  Pdp.prototype.updateInstalments = function (variant) {
    var box = this.root.querySelector('[data-pdp-installments]');
    if (!box) return;

    var amount = box.querySelector('[data-pdp-installment-amount]');
    var months = parseInt(box.getAttribute('data-pdp-installment-months'), 10) || 12;

    // No price, nothing to divide — hide rather than print a zero.
    if (!variant || !variant.price || months < 1) {
      box.hidden = true;
      return;
    }

    box.hidden = false;
    if (amount) {
      amount.textContent = formatMoney(Math.floor(variant.price / months), this.moneyFormat);
    }
  };

  /* ----- delivery fee line ----------------------------------------------
     Same contract as the instalment line: called from inside updatePrice(), so
     the fee cannot disagree with the variant on screen. The fee is whole
     shekels (metafield custom.delivery_fee); only chair-set variants override
     the product's fee, and Liquid prints just those overrides into
     data-pdp-delivery-variants. Formatted through formatMoney() in agorot, then
     ".00" is dropped to match Liquid's money_without_trailing_zeros on first
     paint and the cart's "700 ₪". Fees are whole shekels, so nothing is lost.
     --------------------------------------------------------------------- */
  Pdp.prototype.updateDelivery = function (variant) {
    var box = this.root.querySelector('[data-pdp-delivery]');
    if (!box || !variant) return;
    if (!this.deliveryFees) {
      try { this.deliveryFees = JSON.parse(box.getAttribute('data-pdp-delivery-variants') || '{}'); }
      catch (e) { this.deliveryFees = {}; }
    }
    var fee = this.deliveryFees[String(variant.id)];
    if (fee == null) fee = parseInt(box.getAttribute('data-pdp-delivery-default'), 10) || 0;
    var amount = box.querySelector('[data-pdp-delivery-amount]');
    if (amount) amount.textContent = formatMoney(fee * 100, this.moneyFormat).replace(/[.,]00(?!\d)/, '');
  };

  Pdp.prototype.updateStickyPrice = function (variant) {
    if (!this.sticky) return;
    var el = this.sticky.querySelector('[data-pdp-sticky-price]');
    if (el) el.textContent = formatMoney(variant.price, this.moneyFormat);
  };

  /* ----- 3. AJAX cart --------------------------------------------------- */

  Pdp.prototype.bindSubmit = function () {
    var self = this;
    if (!this.form) return;
    this.form.addEventListener('submit', function (event) {
      if (!window.fetch) return;          // no AJAX: let the native POST happen
      event.preventDefault();
      self.addToCart();
    });
  };

  /**
   * @param {HTMLElement} [origin] - the button that was pressed, so the sticky
   *        bar's button shows its own busy state instead of the main one's.
   */
  Pdp.prototype.addToCart = function (origin) {
    var self = this;
    var button = origin || this.root.querySelector('[data-pdp-atc]');
    var errorEl = this.root.querySelector('[data-pdp-atc-error]');

    if (button && button.getAttribute('aria-busy') === 'true') return;
    if (errorEl) errorEl.textContent = '';

    // Read the id off the master select — the same value the form would post.
    var id = this.master ? Number(this.master.value) : null;
    if (!id) { if (errorEl) errorEl.textContent = this.errorGeneric; return; }

    // Made-to-measure fields, when the fieldset is showing.
    var properties = this.customProperties();
    var propertyCount = Object.keys(properties).length;

    // If the customer chose made-to-measure, the measurements are the order —
    // refuse rather than manufacture something arbitrary.
    if (this.customSize && !this.customSize.hidden) {
      var missing = this.customInputs.some(function (input) {
        return !(input.value || '').trim();
      });
      if (missing) {
        if (errorEl) errorEl.textContent = this.customRequiredMessage();
        var first = this.customInputs.filter(function (i) { return !(i.value || '').trim(); })[0];
        if (first && first.focus) first.focus();
        return;
      }
    }

    if (button) { button.setAttribute('aria-busy', 'true'); button.disabled = true; }

    var item = { id: id, quantity: 1 };
    if (propertyCount) item.properties = properties;

    // Add-ons chosen in the buy box (pdp-bed.js: the mattress) ride in the SAME
    // request, so the cart can never hold the bed without the mattress the
    // customer picked, or the reverse.
    var items = [item];
    if (typeof this.root.pdpExtraItems === 'function') items = items.concat(this.root.pdpExtraItems() || []);

    fetch(cartUrl('/cart/add.js'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: items })
    })
      .then(function (r) { return r.json().then(function (d) { if (!r.ok) throw d; return d; }); })
      .then(function () {
        // The theme's cart section listens for this on document: it re-renders
        // the cart and opens its own drawer. We deliberately do not build one.
        self.root.dispatchEvent(new CustomEvent('product:added', {
          bubbles: true,
          detail: { variant: self.currentVariant || self.variantById(id), quantity: 1 }
        }));
      })
      .catch(function (err) {
        var message = (err && (err.description || err.message)) || self.errorGeneric;
        if (errorEl) errorEl.textContent = message;
      })
      .finally(function () {
        if (button) { button.removeAttribute('aria-busy'); button.disabled = false; }
      });
  };

  /* ----- 5. wishlist ---------------------------------------------------- */

  /**
   * There is more than one heart on the page — one in the buy box and one in
   * the sticky bar — and they are two faces of a SINGLE control, not two
   * independent toggles. Every button carrying [data-pdp-wish] is bound, and a
   * click on any of them repaints all of them from the stored list.
   *
   * Painting from storage rather than from the clicked button's own class keeps
   * them honest: if a write fails (private mode, quota) the UI reflects what was
   * actually saved instead of what we hoped to save.
   */
  Pdp.prototype.bindWishlist = function () {
    var self = this;
    this.wishButtons = toArray(this.root.querySelectorAll('[data-pdp-wish]'));
    if (!this.wishButtons.length) return;

    this.wishId = this.wishButtons[0].getAttribute('data-product-id');
    this.paintWishlist();

    this.wishButtons.forEach(function (button) {
      button.addEventListener('click', function () {
        var list = readWishlist();
        var at = list.indexOf(self.wishId);
        if (at === -1) list.push(self.wishId); else list.splice(at, 1);
        writeWishlist(list);
        self.paintWishlist();
        // The header badge listens for this. `storage` only fires in OTHER
        // tabs, so without it the count would not move until a reload.
        document.dispatchEvent(new CustomEvent('wishlist:changed', {
          detail: { count: list.length }
        }));
      });
    });

    // Another tab may change the list; keep this one in step.
    window.addEventListener('storage', function (event) {
      if (event.key === WISHLIST_KEY) self.paintWishlist();
    });
  };

  /** Repaint every heart from the saved list — the single source of truth. */
  Pdp.prototype.paintWishlist = function () {
    if (!this.wishButtons) return;
    var saved = readWishlist().indexOf(this.wishId) !== -1;

    this.wishButtons.forEach(function (button) {
      button.setAttribute('aria-pressed', saved ? 'true' : 'false');
      button.classList.toggle('is-saved', saved);
    });
  };

  /* ------------------------------------------------------------------ boot */

  function init() {
    toArray(document.querySelectorAll('[data-pdp-root]')).forEach(function (root) {
      if (root.dataset.pdpReady === 'true') return;
      root.dataset.pdpReady = 'true';
      new Pdp(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);
})();
